import { Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import APIUrls from '../../core/ApiUrls';
import Toastify from '../../core/Toastify';
import { formatDate, MakeAxiosRequest } from '../../core/utils';

export interface ReporterWebhook {
    id: string;
    reporter_user_id: string;
    name: string;
    url: string;
    success_rate: number;
    is_deleted: boolean;
    created_at: string;
    updated_at: string;
}

const urlRegex = /^https?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?::\d+)?(?:\/[^\s]*)?$/;

const Webhooks = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [webhooks, setWebhooks] = useState<ReporterWebhook[]>([]);

    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [editWebhookId, setEditWebhookId] = useState<string | undefined>(undefined);
    const [deleteWebhookId, setDeleteWebhookId] = useState<string | undefined>(undefined);
    const [creatingOrUpdating, setCreatingOrUpdating] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const [formName, setFormName] = useState<string>('');
    const [formUrl, setFormUrl] = useState<string>('');

    const isUrlErrored = useMemo(() => !formUrl.trim().match(urlRegex), [formUrl]);
    const isNameErrored = useMemo(() => formName.trim().length === 0, [formName]);

    const resetForm = () => {
        setFormName('');
        setFormUrl('');
    };

    const openCreateModal = () => {
        resetForm();
        setEditWebhookId(undefined);
        setCreateModalOpen(true);
    };

    const openEditModal = (id: string) => {
        const wh = webhooks.find(w => w.id === id);
        if (wh) {
            setFormName(wh.name || '');
            setFormUrl(wh.url || '');
        }
        setEditWebhookId(id);
        setCreateModalOpen(true);
    };

    const fetchWebhooks = async () => {
        try {
            const res = await MakeAxiosRequest('GET', APIUrls.getURL('WEBHOOKS'));
            if (res?.status === 200) {
                setWebhooks(res?.data?.data?.webhooks ?? []);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchWebhooks();
    }, []);

    const createWebhookApi = async (payload: { name: string; url: string }) => {
        await MakeAxiosRequest('POST', APIUrls.getURL('WEBHOOKS'), {
            command: 'CREATE',
            name: payload.name,
            url: payload.url,
        });
    };
    const updateWebhookApi = async (id: string, payload: { name: string; url: string }) => {
        await MakeAxiosRequest('POST', APIUrls.getURL('WEBHOOKS'), {
            command: 'EDIT',
            id,
            name: payload.name,
            url: payload.url,
        });
    };
    const deleteWebhookApi = async (id: string) => {
        await MakeAxiosRequest('POST', APIUrls.getURL('WEBHOOKS'), {
            command: 'DELETE',
            id,
        });
    };

    const handleSubmit = async () => {
        const name = formName.trim();
        const url = formUrl.trim();
        if (!name || !url.match(urlRegex)) {
            Toastify.error('Please fill valid details');
            return;
        }
        try {
            setCreatingOrUpdating(true);
            if (editWebhookId) {
                await updateWebhookApi(editWebhookId, { name, url });
                Toastify.success('Webhook updated');
            } else {
                await createWebhookApi({ name, url });
                Toastify.success('Webhook created');
            }
            setCreateModalOpen(false);
            resetForm();
        } catch (error) {
            console.log(error);
        } finally {
            setCreatingOrUpdating(false);
            fetchWebhooks();
        }
    };

    const handleDelete = async (id?: string) => {
        if (!id) {
            Toastify.error('Error deleting this webhook');
            return;
        }
        try {
            setDeleting(true);
            await deleteWebhookApi(id);
            Toastify.success('Webhook deleted');
            setDeleteWebhookId(undefined);
        } catch (error) {
            console.log(error);
        } finally {
            setDeleting(false);
            fetchWebhooks();
        }
    };

    return (
        <div className="w-full overflow-x-auto">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Webhooks</h1>
                <button onClick={openCreateModal} disabled={isLoading} className="text-sm bg-gradient-purple hover:hover-gradient-purple disabled:bg-gray-400 disabled:cursor-not-allowed transition text-white py-3 px-6 rounded-xl font-semibold shadow-sm whitespace-nowrap flex items-center gap-2">
                    {isLoading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                    {isLoading ? 'Loading...' : 'Create Webhook'}
                </button>
            </div>
            {isLoading && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin h-10 w-10 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                </div>
            )}
            {!isLoading &&
                (webhooks && webhooks.length > 0 ? (
                    <div className="bg-white border-collapse border-[1px] border-gray-300 rounded-xl shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-max">
                                <thead className="border-b border-gray-200 bg-white">
                                    <tr>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">URL</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Success Rate</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created At</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider pr-6">Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {webhooks.map(wh => (
                                        <tr key={wh.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4">
                                                <div className="min-w-0 pl-2">
                                                    <div className="text-sm font-medium text-gray-900 truncate">{wh.name}</div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="truncate max-w-[220px]">
                                                    <a href={wh.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                                        {wh.url}
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-900">{Math.round(wh.success_rate)}%</div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${wh.is_deleted ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{wh.is_deleted ? 'Deleted' : 'Active'}</span>
                                            </td>
                                            <td className="px-4 py-4 flex flex-row gap-3 text-sm">
                                                <button onClick={() => openEditModal(wh.id)} className="bg-gradient-purple hover:hover-gradient-purple transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    <Pencil size={18} />
                                                </button>
                                                <button onClick={() => setDeleteWebhookId(wh.id)} className="bg-red-600 hover:bg-red-500 transition text-white p-2 rounded-xl font-semibold shadow-sm whitespace-nowrap">
                                                    <Trash2 size={20} />
                                                </button>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{formatDate(wh.created_at)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500 pr-6">{formatDate(wh.updated_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                        <div className="text-gray-400 mb-4">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Webhooks Configured</h3>
                        <p className="text-gray-500 mb-6">Get started by creating your first webhook.</p>
                        <button className="bg-gradient-purple hover:hover-gradient-purple transition text-white py-2 px-6 rounded-xl font-semibold shadow-sm" onClick={openCreateModal}>
                            Create Webhook
                        </button>
                    </div>
                ))}

            <Modal loading={creatingOrUpdating} isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} onConfirm={handleSubmit} showFooter={true} title={editWebhookId ? 'Edit Webhook' : 'Create Webhook'} width="w-2/5" height="max-h-3/4 h-fit">
                <div className="flex flex-col gap-3">
                    <Input isErrored={isNameErrored} value={formName} onChange={setFormName} placeholder="Enter Name" />
                    <Input isErrored={isUrlErrored} value={formUrl} onChange={setFormUrl} placeholder="Enter Webhook URL" />
                </div>
            </Modal>

            <Modal loading={deleting} isOpen={!!deleteWebhookId} onClose={() => setDeleteWebhookId(undefined)} onConfirm={() => handleDelete(deleteWebhookId!)} title={`Are you sure you want to delete this webhook?`}>
                {deleteWebhookId &&
                    (() => {
                        const wh = webhooks.find(it => it.id === deleteWebhookId);
                        return (
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-sm text-gray-600 mb-2">
                                        <strong>Name:</strong> {wh?.name || 'Unknown'}
                                    </p>
                                    <p className="text-sm text-gray-600 mb-2">
                                        <div className="truncate max-w-full">
                                            <strong>URL: </strong>
                                            <a href={wh?.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                                {wh?.url}
                                            </a>
                                        </div>
                                    </p>
                                </div>
                            </div>
                        );
                    })()}
            </Modal>
        </div>
    );
};

export default Webhooks;
